import { IconMinus, IconPlus } from '@tabler/icons-react'
import { useState } from 'react'
import { SetRequired } from 'type-fest'

import { AstraButton, AstraIcon, AstraInputWrapper, AstraPractitionerChip, AstraSelect, AstraTextField, getToday } from '~/astra'
import { getErrorMessageByStatusCode } from '~/hooks/tanstack-query'
import { useFlag } from '~/hooks/useFlag'
import { dispatchErrorToast } from '~/store/slices/toasterSlice'
import { useSpecialities } from '~/storm'
import { getSpecialityName } from '~/storm/common/specialty'

import { DatePickerField } from '../../Form/DatePickerField'
import { useAppForm } from '../../Form/formHooks'
import { FieldGroupWrapper, FormContentWrapper, FormSectionWrapper } from '../../Form/FormWrapper'
import { ProfileCollapsibleCard } from '../../Form/ProfileCollapsibleCard'
import { createFormFieldProps } from '../../Form/utils'
import { defaultFormValues, PractitionerFormDefaultValues, PractitionerFormParsedValues, practitionerFormSchema } from './formState'
import { ProfileFields } from './ProfileFields'
import { EmptyServicePeriods, ServicePeriodGroupField } from './ServicePeriodGroupField'

export type PractitionerFormInitialValues = SetRequired<Partial<PractitionerFormParsedValues>, 'shortName'>

export type OnSubmitFunction = (parsedValues: PractitionerFormParsedValues, defaultValues: PractitionerFormDefaultValues) => Promise<void>
export type PractitionerFormProps = {
    initialValues: PractitionerFormInitialValues
    onSubmit: OnSubmitFunction
    title: string
    isNew?: boolean
}

export const PractitionerForm = ({ initialValues, onSubmit, title, isNew = false }: PractitionerFormProps) => {
    const specialities = useSpecialities()
    const isExpertEnabled = useFlag('hospital_configuration_expert')

    const [defaultValues, setDefaultValues] = useState<typeof defaultFormValues>(() => ({
        ...defaultFormValues,
        ...initialValues,
    }))

    const form = useAppForm({
        defaultValues,
        validators: { onChange: practitionerFormSchema },
        onSubmit: async ({ value, formApi }) => {
            const parsed = practitionerFormSchema.parse(value)
            try {
                await onSubmit(parsed, defaultValues)
                formApi.reset(parsed)
                setDefaultValues(parsed)
            } catch (e) {
                console.error('Failed to submit practitioner', e)
                dispatchErrorToast({ message: getErrorMessageByStatusCode() })
            }
        },
        canSubmitWhenInvalid: true,
    })

    return (
        <FormContentWrapper title={title}>
            <div className="flex flex-col gap-8">
                <FormSectionWrapper title="Om kirurgen">
                    <FieldGroupWrapper>
                        <form.Field name="shortName">
                            {field => (
                                <AstraInputWrapper helperText="Hentet fra DIPS" fullWidth label="Initialer" disabled className="p-1" labelHtmlFor={field.name}>
                                    <AstraPractitionerChip shortName={field.state.value} size="medium" />
                                </AstraInputWrapper>
                            )}
                        </form.Field>
                        <form.Field name="fullName">
                            {field => (
                                <AstraTextField
                                    helperText="Hentet fra DIPS"
                                    fullWidth
                                    className="p-2"
                                    label="Fullt navn"
                                    value={field.state.value}
                                    placeholder="Fullt navn"
                                    size="medium"
                                    disabled
                                />
                            )}
                        </form.Field>
                    </FieldGroupWrapper>

                    <FieldGroupWrapper>
                        <form.Field name="specialityId">
                            {field => (
                                <AstraSelect
                                    name={field.name}
                                    label="Fagområde"
                                    options={specialities.map(speciality => ({ label: getSpecialityName(speciality), value: speciality.id }))}
                                    value={field.state.value}
                                    {...createFormFieldProps(field, { blurOnChange: true })}
                                />
                            )}
                        </form.Field>
                    </FieldGroupWrapper>
                </FormSectionWrapper>
                <form.Field name="servicePeriods">
                    {servicePeriodsField => {
                        const hasNoPeriods = servicePeriodsField.state.value.length === 0
                        const allPeriodsEmpty = servicePeriodsField.state.value.every(period => period.startDate === undefined && period.endDate === undefined)
                        return (
                            <FormSectionWrapper
                                title="Tjenesteperiode"
                                description="Viser perioden kirurgen er aktiv i Hero. Kirurgen vil bli skjult utenfor aktiv tjenesteperiode. "
                                noPadding={hasNoPeriods}
                                noBorder={hasNoPeriods}
                                titleAction={
                                    <AstraButton
                                        variant="secondary"
                                        type="button"
                                        label="Legg til tjenesteperiode"
                                        icon={<AstraIcon icon={IconPlus} />}
                                        onClick={() => form.pushFieldValue('servicePeriods', {})}
                                    />
                                }
                            >
                                <div className="flex flex-col gap-4">
                                    {servicePeriodsField.state.value.map((_, index) => (
                                        <div className="flex items-start justify-around gap-4 pl-4" key={index}>
                                            <ServicePeriodGroupField form={form} fields={`servicePeriods[${index}]`} />
                                            <div className="pt-5">
                                                <AstraButton
                                                    variant="secondary"
                                                    type="button"
                                                    icon={<AstraIcon icon={IconMinus} />}
                                                    onClick={() => servicePeriodsField.removeValue(index)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    {(hasNoPeriods || allPeriodsEmpty) && <EmptyServicePeriods />}
                                </div>
                            </FormSectionWrapper>
                        )
                    }}
                </form.Field>

                {isExpertEnabled && (
                    <>
                        <ProfileFields form={form} fields={'activeProfile'} />

                        <FormSectionWrapper
                            title="Fremtidige kirurgprofiler"
                            noBorder
                            noPadding
                            description="Viser fremtidige endringer i kirurgprofilen."
                            titleAction={
                                <AstraButton
                                    variant="secondary"
                                    type="button"
                                    label="Legg til kirurgprofil"
                                    icon={<AstraIcon icon={IconPlus} />}
                                    onClick={() => {
                                        const lastFutureProfile = form.getFieldValue('futureProfiles').at(-1)
                                        const active = lastFutureProfile ?? form.getFieldValue('activeProfile')
                                        const newProfile = {
                                            ...active,
                                            id: undefined,
                                            activeFrom: undefined,
                                        }
                                        form.pushFieldValue('futureProfiles', newProfile)
                                    }}
                                />
                            }
                        >
                            <form.Field name="futureProfiles">
                                {profilesField =>
                                    profilesField.state.value.map((profile, index, arr) => (
                                        <form.Field name={`futureProfiles[${index}].activeFrom`} key={profile.id ?? index}>
                                            {field => (
                                                <ProfileCollapsibleCard
                                                    title="Fremtidig kirurgprofil"
                                                    activeFrom={field.state.value}
                                                    nextProfileActiveFrom={profilesField.state.value.at(index + 1)?.activeFrom ?? undefined}
                                                    defaultOpen={profile.id === undefined && index === arr.length - 1}
                                                    // reset the defaultOpen state when a new profile is added
                                                    key={arr.length}
                                                    onRemove={() => profilesField.removeValue(index)}
                                                >
                                                    <DatePickerField
                                                        label="Aktiv fra"
                                                        value={field.state.value}
                                                        startMonth={
                                                            form.getFieldValue('futureProfiles').at(index - 1)?.activeFrom ??
                                                            form.getFieldValue('activeProfile').activeFrom ??
                                                            getToday()
                                                        }
                                                        {...createFormFieldProps(field, { blurOnChange: true })}
                                                    />
                                                    <ProfileFields form={form} fields={`futureProfiles[${index}]`} size="compact" />
                                                </ProfileCollapsibleCard>
                                            )}
                                        </form.Field>
                                    ))
                                }
                            </form.Field>
                        </FormSectionWrapper>
                    </>
                )}
            </div>

            <form.AppForm>{isNew ? <form.FormSaveNewControl /> : <form.FormFloatingControl />}</form.AppForm>
        </FormContentWrapper>
    )
}
